"use client";

import { useState, useEffect } from "react";
import { BookOutlined, LockOutlined, SoundOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import BookActions from "./BookAction";
import { useAudio } from "../../context/AudioProvider";
import { IBookTypeData } from "../../apiservice/userService.type";
import { userGetBookSingle } from "../../apiservice/userService";
import { Modal, Spin } from "antd";
import { appZIndex } from "../../utils/appconst";
import PaymentOnboarding from "../sharedcomponents/PaymentOnboarding";
import "./bookcomp.css";
import { Device } from "@capacitor/device";

interface BookSummaryProps {
  bookId?: string;
}

const BookSummary = ({ bookId }: BookSummaryProps) => {
  const {
    setAudioSource,
    currentMusic,
    setMusicList,
    currentPlayerUrl,
    setCurrentPlayerUrl,
  } = useAudio();

  const params = useParams();
  const id = bookId || params.id || "1";
  const navigate = useNavigate();

  const [showDetails, setShowDetails] = useState(true);
  const [book, setBook] = useState<IBookTypeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [usePaddedLogic, setUsePaddedLogic] = useState(false);


  useEffect(() => { getDeviceOSVersion(); }, [])


  const getDeviceOSVersion = async () => {
    try {
      const info = await Device.getInfo();
      console.log('Device Information:', info);

      const platform = info.operatingSystem; // e.g., 'ios' or 'android'
      const osApiLevel = info.androidSDKVersion;

      if (platform === "ios") {
        setUsePaddedLogic(true);
      }

      if ((osApiLevel || 0) >= 35) {
        setUsePaddedLogic(true);
      }

    } catch (error) {
      console.error('Error getting device info:', error);
      return null;
    }
  };



  useEffect(() => {
    fetchSingleBoook();
  }, [id]);

  const fetchSingleBoook = async () => {
    try {
      const bookData = await userGetBookSingle(id);
      if (bookData) {
        setBook(bookData?.data);
      } else {
        setError("Book not found");
      }
    } catch (err) {
      setError("Failed to load book data");
    } finally {
      setLoading(false);
    }
  };

  // Handle payment button click
  const handlePayment = () => {
    setPaymentModalVisible(true);
  };

  if (loading) {
    return (
      <div className="p-4 text-center text-gray-900 dark:text-stone-100">
        <Spin size="large" /> &nbsp; Loading book details...
      </div>
    );
  }

  if (error || !book) {
    return (
      <div className="p-4 text-center text-red-500">
        {error || "Book not found"}
      </div>
    );
  }

  return (
    // Assuming stone-900 is a very dark background for the page body from index.css
    <div className="bg-white dark:bg-stone-800 p-4 rounded-lg pb-20 relative">
      {/* Book Cover and Header */}
      <div className="relative flex justify-center">
        <img
          src={book.coverImageUrl || "/placeholder.svg"}
          alt={book.title}
          className="w-1/2 max-w-[400px] rounded-lg"
        />
        {book.isNewRelease && (
          <div className="absolute top-2 left-2 bg-orange-500 text-white px-2 py-1 text-xs rounded">
            New Release
          </div>
        )}
      </div>

      {/* Action Icons Card */}
      <div className="w-full flex justify-center mt-4">
        <BookActions book={book} />
      </div>

      {/* Summary */}
      <div className="mt-4">
        <h3 className="text-2xl font-semibold text-gray-900 dark:text-stone-100">
          {book.title}
        </h3>
        <p className="text-lg text-gray-600 dark:text-stone-400">
          {book.authorName}
        </p>
      </div>

      {/* What's Inside */}
      <div className="mt-4">
        <h4 className="text-2xl font-semibold text-gray-900 dark:text-stone-100">
          What's inside?
        </h4>
        <div
          className="text-lg text-gray-600 dark:text-stone-400"
          dangerouslySetInnerHTML={{
            __html: book?.summary || "",
          }}
        ></div>
        <div className="mt-2 border dark:border-stone-700 p-3 rounded-lg">
          <h5 className="font-semibold text-gray-900 dark:text-stone-100">
            You'll learn
          </h5>
          <ul className="list-none text-lg text-gray-700 dark:text-stone-300 space-y-1">
            {book.learningPoints.map((point, index) => (
              <li key={index}>&#10003; {point.title}</li>
            ))}
          </ul>
        </div>
      </div>

      {/* Key Points Toggle */}
      <button
        onClick={() => setShowDetails(!showDetails)}
        className="mt-4 text-blue-500 dark:text-blue-400 font-semibold"
      >
        Key Points {showDetails ? "▲" : "▼"}
      </button>

      {/* Key Points */}
      {showDetails && (
        <div className="mt-4">
          {book.keyPoints.map((point, index) => (
            <div
              key={index}
              className="py-2 border-b dark:border-stone-700 text-gray-700 dark:text-stone-300 flex justify-between"
            >
              <span>
                {index + 1}. {point.title}
              </span>
              <span className="text-blue-500 dark:text-blue-400">&gt;</span>
            </div>
          ))}
        </div>
      )}

      {/* Author Info */}
      <div className="mt-4 text-lg text-gray-600 dark:text-stone-400">
        <h4 className="text-2xl font-semibold text-gray-900 dark:text-stone-100">
          About {book.authorName}
        </h4>
        <p>{book.author.bio}</p>
        {book.disclaimer && (
          <p className="text-xs mt-2 text-gray-500 dark:text-stone-500">
            {book.disclaimer}
          </p>
        )}

        <p style={{paddingTop:"40px"}}>

        </p>
      </div>

      {/* Fixed Buttons */}
      <div className={` ${usePaddedLogic ? " safeAreaBottomios " : " "}   fixed bottom-0 left-0  w-full bg-white dark:bg-stone-800 p-4 shadow-md flex justify-between border-t border-gray-200 dark:border-stone-700`}>
        {/* <button className="flex items-center gap-2 bg-gray-200 dark:bg-stone-700 px-4 py-2 rounded-lg text-gray-700 dark:text-stone-200 hover:bg-gray-300 dark:hover:bg-stone-600">
          <BookOutlined /> Read
        </button> */}
        <button
          onClick={() => {
            // Temporary Access
            if (!book?.hasAccess) {
              handlePayment();
              return;
            }
            if (!book.audios.length) {
              alert(
                "No Audio File is found, we will notify once there is an audio file."
              );
              return;
            }

            if (currentMusic?.musicFile !== book.mainAudio?.fileName) {
              // setAudioSource(book.audioFile || "")
              if (book.audios) {
                setMusicList(
                  (book?.audios || []).map((mapFile) => {
                    return {
                      id: mapFile.id + "",
                      musicFile: mapFile?.fileUrl || "",
                      // musicFile:
                      //   "https://next-js-practice-keyway-app.vercel.app/SampleMusic/sample.mp3",
                      title: book?.title + "--- " + mapFile?.fileName || "",
                      coverImage: book?.coverImageUrl || "",
                      duration: mapFile.duration || "2:40",
                      artist: book.authorName,
                      description: mapFile.subTitle,
                    };
                  })
                );
              } else {
                setMusicList([
                  {
                    id: book.mainAudio?.id + "",
                    musicFile: book.mainAudio?.fileUrl || "",
                    // musicFile:
                    //   "https://next-js-practice-keyway-app.vercel.app/SampleMusic/sample.mp3",
                    title: book?.title || "",
                    coverImage: book?.coverImageUrl || "",
                    duration: book.mainAudio?.duration,
                    artist: book.authorName,
                    description: book.mainAudio.subTitle,
                  },
                ]);
              }
            }
            setCurrentPlayerUrl(
              `/users/book-details-audio/${book.id}/${encodeURIComponent(
                book.title.replace(/\s+/g, "_")
              )}`
            );
            navigate(
              `/users/book-details-audio/${book.id}/${encodeURIComponent(
                book.title.replace(/\s+/g, "_")
              )}`
            );
          }}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg text-white"
        >
          <SoundOutlined /> Listen To Book Summary{" "}
          {!book?.hasAccess && (
            <LockOutlined style={{ fontSize: "20px", color: "#fff" }} />
          )}
        </button>
      </div>

      {/* Payment Modal */}
      <Modal
        title={
          <span className="text-gray-900 dark:text-stone-100">{`Subscription`}</span>
        }
        open={paymentModalVisible}
        onCancel={() => setPaymentModalVisible(false)}
        footer={null}
        zIndex={appZIndex.modal}
        width={600}
        className="[&_.ant-modal-content]:bg-white [&_.ant-modal-content]:dark:bg-stone-900 [&_.ant-modal-header]:bg-white [&_.ant-modal-header]:dark:bg-stone-900 [&_.ant-modal-header]:border-b [&_.ant-modal-header]:border-gray-200 [&_.ant-modal-header]:dark:border-stone-700 [&_.ant-modal-body]:bg-white [&_.ant-modal-body]:dark:bg-stone-900 [&_.ant-modal-body]:max-h-[70vh] [&_.ant-modal-body]:overflow-y-auto"
      >
        <div className="bg-transparent">
          <PaymentOnboarding />
        </div>
      </Modal>
    </div>
  );
};

export default BookSummary;
